/**
 * SessionGate — проверка сохранённой сессии до отрисовки приложения.
 *
 * Симптом, из-за которого это появилось: пользователь открывал
 * админку, успевал увидеть страницу со статистикой — и только потом
 * его выбрасывало на логин. Выглядело так, будто вход происходит, а
 * потом отменяется.
 *
 * Причина: токен и выбранный доступ лежат в localStorage и читаются
 * синхронно (`getOnInit`), поэтому `isAuthenticated` становится
 * `true` мгновенно, ещё до единого запроса к бэку. Приложение
 * рисовалось целиком, запросы уходили с протухшим токеном, и логаут
 * случался только когда первый из них возвращал 401 — то есть через
 * пару сетевых round-trip'ов. Всё это время на экране висел интерфейс
 * с пустыми и полупустыми данными.
 *
 * Теперь при старте с сохранённой сессией сначала спрашиваем бэк,
 * жив ли токен (`auth/permission` — самый дешёвый авторизованный
 * запрос), и только потом пускаем внутрь. Пока проверяем — сплэш.
 * Токен мёртв → чистый логаут и логин-экран, без мелькания админки.
 *
 * Проверяем один раз за загрузку страницы: дальше протухание ловит
 * обработчик 401 в queryClient.
 */

import {useEffect, useRef, useState} from 'react';
import {Spin} from 'antd';
import {useAtomValue} from 'jotai';

import {getPermissions} from '@services/auth';
import {HttpError} from '@services/utils';
import {isAuthenticatedAtom} from '@atoms/auth';
import {useAuth} from '@hooks/useAuth';

import styles from './SessionGate.module.scss';

type Props = {children: React.ReactNode};

export const SessionGate = ({children}: Props) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const {logout} = useAuth();

  /* Проверять нечего, если сессии нет: сразу пускаем к роутеру, он
     сам покажет логин. */
  const [checking, setChecking] = useState(isAuthenticated);
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current || !isAuthenticated) return;
    doneRef.current = true;

    let alive = true;
    getPermissions()
      .then(() => {
        if (alive) setChecking(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // 401/403 — токен мёртв, выкидываем сразу и молча.
        // Сеть легла — оставляем сессию: интернет вернётся, а
        // разлогинивать человека из-за отвалившегося вайфая незачем.
        if (e instanceof HttpError && (e.status === 401 || e.status === 403)) {
          logout();
        }
        setChecking(false);
      });

    return () => {
      alive = false;
    };
    // logout стабилен по смыслу (сеттеры jotai), пере-подписка не нужна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (checking) {
    return (
      <div className={styles.splash}>
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};
